import { useEffect, useRef, useState } from 'react';

const values = [
  {
    icon: 'ri-microscope-line',
    title: 'Rigor científico',
    desc: 'Trabajamos con evidencia, metodologías verificables y criterios técnicos sólidos.',
  },
  {
    icon: 'ri-loop-left-line',
    title: 'Reproducibilidad',
    desc: 'Diseñamos procesos claros, documentados y trazables que permiten verificar y auditar resultados.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Robustez',
    desc: 'Desarrollamos análisis y modelos sólidos frente a la variabilidad de los datos y contextos.',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Claridad',
    desc: 'Transformamos datos complejos en conocimiento comprensible y útil para la toma de decisiones.',
  },
  {
    icon: 'ri-links-line',
    title: 'Integración',
    desc: 'Conectamos disciplinas, tecnologías, instituciones y conocimiento científico.',
  },
  {
    icon: 'ri-rocket-line',
    title: 'Innovación responsable',
    desc: 'Aplicamos tecnología con fundamento científico, utilidad real y responsabilidad social.',
  },
  {
    icon: 'ri-earth-line',
    title: 'Impacto humano',
    desc: 'Orientamos nuestro trabajo hacia la salud, la biodiversidad, el ambiente y la sociedad.',
  },
  {
    icon: 'ri-lock-line',
    title: 'Confianza',
    desc: 'Construimos relaciones basadas en transparencia, confidencialidad y calidad técnica.',
  },
];

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

export default function AboutSection() {
  const { ref: sectionRef, inView } = useInView(0.1);

  return (
    <section id="about" ref={sectionRef} className="relative py-20 md:py-32 bg-biodata-dark overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-radial from-biodata-teal/5 to-transparent pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`max-w-3xl mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-biodata-teal/10 border border-biodata-teal/20 mb-4">
            <span className="text-xs text-biodata-teal font-medium uppercase tracking-wider">Nosotros</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            ¿Quiénes <span className="text-gradient">somos?</span>
          </h2>
          <p className="text-biodata-muted text-base md:text-lg leading-relaxed mb-6">
            <strong className="text-white">ANALITICA</strong> es una empresa mexicana especializada en investigación 
            científica aplicada, bioinformática, biología de sistemas, ciencia de datos e infraestructura 
            analítica. Su misión es transformar datos biológicos, biomédicos y ambientales en conocimiento 
            útil, reproducible e interpretable para impulsar la investigación, la innovación y la toma de decisiones.
          </p>
        </div>

        {/* Mission & Vision Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {/* Mission */}
          <div
            className={`glass-card rounded-2xl p-6 md:p-8 transition-all duration-700 delay-100 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-biodata-teal/15 flex items-center justify-center flex-shrink-0">
                <i className="ri-focus-3-line text-xl text-biodata-teal" />
              </div>
              <div>
                <h3 className="text-xl font-display font-semibold text-white mb-2">Misión</h3>
                <p className="text-biodata-muted text-sm leading-relaxed">
                  Integrar ciencia, datos, modelado computacional e infraestructura tecnológica para resolver 
                  problemas complejos en sistemas vivos, transformando datos biológicos, biomédicos y ambientales 
                  en conocimiento reproducible, interpretable y útil. ANALITICA crea puentes entre la investigación, 
                  la industria y el gobierno para desarrollar soluciones con impacto humano, biomédico, ambiental y social.
                </p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden aspect-video mt-4">
              <img
                src="https://readdy.ai/api/search-image?query=Scientific%20data%20analysis%20workspace%20with%20holographic%20displays%20showing%20genomic%20sequences%20and%20statistical%20charts%2C%20modern%20laboratory%20with%20teal%20ambient%20lighting%2C%20futuristic%20bioinformatics%20research%20environment%2C%20clean%20minimal%20aesthetic%2C%20dark%20moody%20atmosphere&width=600&height=340&seq=about1&orientation=landscape"
                alt="Laboratorio de bioinformática ANALITICA"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          {/* Vision */}
          <div
            className={`glass-card rounded-2xl p-6 md:p-8 transition-all duration-700 delay-200 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-biodata-emerald/15 flex items-center justify-center flex-shrink-0">
                <i className="ri-eye-line text-xl text-biodata-emerald" />
              </div>
              <div>
                <h3 className="text-xl font-display font-semibold text-white mb-2">Visión</h3>
                <p className="text-biodata-muted text-sm leading-relaxed">
                  Consolidarse como una firma científica mexicana de referencia internacional en ciencias de 
                  la vida, bioinformática, biología de sistemas, ciencia de datos e infraestructura tecnológica, 
                  articulando investigación, industria y gobierno para generar soluciones estratégicas que 
                  contribuyan a la salud, la biodiversidad, el medio ambiente y el bienestar humano.

                </p>
                <br />
              </div>
            </div>
          
            <div className="rounded-xl overflow-hidden aspect-video mt-4">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20artificial%20intelligence%20brain%20connected%20to%20DNA%20strands%20and%20molecular%20structures%2C%20dark%20background%20with%20glowing%20teal%20and%20green%20neural%20pathways%2C%20biotechnology%20meets%20machine%20learning%20concept%20art%2C%20premium%20scientific%20visualization&width=600&height=340&seq=about2&orientation=landscape"
                alt="IA y biotecnología integradas"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Values */}
        <div
          className={`transition-all duration-700 delay-300 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h3 className="text-2xl font-display font-semibold text-white mb-8 text-center">
            Nuestros <span className="text-gradient">Valores</span>
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="glass-card rounded-xl p-6 group hover:border-biodata-teal/40 transition-all duration-300"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-biodata-teal/10 flex items-center justify-center mb-4 group-hover:bg-biodata-teal/20 transition-colors">
                  <i className={`${value.icon} text-lg text-biodata-teal`} />
                </div>
                <h4 className="text-white font-medium mb-2">{value.title}</h4>
                <p className="text-biodata-muted text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}